import { Injectable, Injector } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { ContainerService, PartService, PartHandlingService, PartLoadingService, DieService, ToolSetService, PressService } from '../../../phf.service';
import { FormGroup, FormBuilder } from '@angular/forms';
import { CgpConfirmationDialogService } from "../../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service";
import { CgpAlertDialogService } from "../../../../../shared/cgp-alert-dialog/cgp-alert-dialog.service";
import { S3Service } from '../../../../../shared/services/s3.service';

const defaultGuid = '00000000-0000-0000-0000-000000000000';

@Injectable()
export class PartDetailService {
  private readonly editingStatus = new BehaviorSubject<any>({ editing: false, saving: false });
  isShowPartDetail: Subject<boolean> = new BehaviorSubject<boolean>(true);
  isLoading = false
  isSaving: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private readonly saveContainerWarningText = "Save Container Warning";
  // Part Detail's Components formGroup References
  partDataObject: any;
  containerDataObject: any;
  partHandlingDataObject: any;
  partLoadingDataObject: any;
  pressDataObject: any;
  dieDataObject: any;
  partDetailHeaderDataObject: any;
  pressId: any;
  fullPartDetail: FormGroup;
  showZoomInDialog = false;
  zoomInData: any;

  private readonly containerService: ContainerService;
  private readonly partService: PartService;
  private readonly partHandlingService: PartHandlingService;
  private readonly partLoadingService: PartLoadingService;
  private readonly dieService: DieService;
  private readonly toolSetService: ToolSetService;
  private readonly pressService: PressService;
  private readonly cgpConfirmationDialog: CgpConfirmationDialogService;
  private readonly cgpAlertDialog: CgpAlertDialogService;
  private readonly s3Service: S3Service;
  private readonly formBuilder: FormBuilder;

  constructor(private readonly injector: Injector) {
    this.containerService = injector.get(ContainerService);
    this.partService = injector.get(PartService);
    this.partHandlingService = injector.get(PartHandlingService);
    this.partLoadingService = injector.get(PartLoadingService);
    this.dieService = injector.get(DieService);
    this.toolSetService = injector.get(ToolSetService);
    this.pressService = injector.get(PressService);
    this.cgpConfirmationDialog = injector.get(CgpConfirmationDialogService);
    this.cgpAlertDialog = injector.get(CgpAlertDialogService);
    this.s3Service = injector.get(S3Service);
    this.formBuilder = injector.get(FormBuilder);
    this.fullPartDetail = this.formBuilder.group(fullPartDetailEditForm);
  }

  /**
  * Opens Editing
  */
  edit() {
    this.editingStatus.next({ editing: true, saving: false });
  }

  /**
  * Closes Editing and Saves changes
  */
  save(continueEditing: boolean) {
    if (!continueEditing) this.editingStatus.next({ editing: false, saving: true });
    else this.editingStatus.next({editing: true, saving: true});
    this.assignFormGroup(this.partDataObject, this.partDetailHeaderDataObject);

    const oldPress = this.pressId;
    const newPress = this.partDataObject.get('PressId').value;
    const partId = this.partDataObject.get('Id').value;

    if (!partId) { // Comment out to disable Part Saving
      this.createPartDetail();
    }
    else if (partId && newPress !== oldPress) {
      this.displayPartReallocationDialog();
    }
    else if (this.partDataObject.get('Id').value) { // Comment out to disable Part Editing
      this.updatePartDetail();
    }
  }

  /**
  * Closes Editing and doesn't Save changes
  */
  close() {
    this.editingStatus.next({ editing: false, saving: false });
  }

  /**
  * Subscribes the component to the isEditing status of Part Detail
  */
  getMessage(): Observable<any> {
    return this.editingStatus.asObservable();
  }

  /**
  * Assign all values of the source FormGroup into the target FormGroup for all
  * matching keys. The source FormGroup can have extra keys. All unmatched keys
  * in the target FormGroup will keep its original value.
  * @param target the FormGroup that the key and values will be matched
  * @param source the FormGroup with key/value pairs that match the target FormGroup
  */
  assignFormGroup(target: FormGroup, source: FormGroup) {
    if (target && source) {
      for (const key in source.value) {
        if (key in target.value) {
          target.get(key).setValue(source.get(key).value);
        }
      }
    }
  }

  /**
  * PUT's all DTO's of a Part
  */
  updatePartDetail() {
    this.isLoading = true;
    Promise.all([
      this.updateContainer(),
      this.updateDie(),
      this.updatePartHandling(),
      this.updatePartLoading(),
      this.updatePart(),
    ])
      .then(() => this.isLoading = false)
      .catch(() => this.isLoading = false)

  }

  /**
  * PUTS a Part's Container DTO
  */
  updateContainer(): Promise<any> {
    if (this.containerDataObject && this.containerDataObject.get('Id').value) {
      if (!this.isDataObjectEmpty(this.containerDataObject) && !this.containerDataObject.get('Number').value) {
        this.cgpAlertDialog.showAlertDialog({
          title: this.saveContainerWarningText,
          message: `The container info will not be saved without a container id. Please enter a container id`,
          alert: 'danger',
          onClosed: () => {
            this.isLoading = false;
            this.edit();
          }
        });
      }
      else {
        this.containerDataObject.get('IsActive').setValue(true);
        if (this.containerDataObject.get('AttachmentFileInfo').value) { this.uploadEventHandler('container', this.containerDataObject.get('AttachmentFileInfo').value); }
        return this.containerService.save(this.containerDataObject.value, false, "/" + this.containerDataObject.get('Id').value);
      }
    }
    else {
      return this.createContainer();
    }
    return Promise.resolve();
  }

  /**
  * PUTS a Part's DIE DTO
  */
  updateDie(): Promise<any> {
    if (this.dieDataObject && this.dieDataObject.get('Id').value) {
      this.dieDataObject.get('IsActive').setValue(true);
      return this.dieService.save(this.dieDataObject.value, false, "/" + this.dieDataObject.get('Id').value);
    }
    return this.createDie();
  }

  /**
  * PUTS a Part's Handling DTO
  */
  updatePartHandling(): Promise<any> {
    if (this.partHandlingDataObject && this.partHandlingDataObject.get('Id').value) {
      this.partHandlingDataObject.get('IsActive').setValue(true);
      return this.partHandlingService.save(this.partHandlingDataObject.value, false, "/" + this.partHandlingDataObject.get('Id').value);
    }
    return this.createPartHandling();
  }

  /**
  * PUTS a Part's Loading DTO
  */
  updatePartLoading(): Promise<any> {
    if (this.partLoadingDataObject && this.partLoadingDataObject.get('Id').value) {
      this.partLoadingDataObject.get('IsActive').setValue(true);
      return this.partLoadingService.save(this.partLoadingDataObject.value, false, "/" + this.partLoadingDataObject.get('Id').value);
    }
    return this.createPartLoading();
  }

  /**
  * PUTS a Part's DTO
  */
  updatePart(): Promise<any> {
    if (this.partDataObject && this.partDataObject.get('Id').value) {
      this.partDataObject.get('IsActive').setValue(true);
      if (this.partDetailHeaderDataObject.get('AttachmentFileInfo').value) {
        this.uploadEventHandler('part', this.partDetailHeaderDataObject.get('AttachmentFileInfo').value);
      }
      return this.partService.save(this.partDataObject.value, false, "/" + this.partDataObject.get('Id').value);
    }
    return null;
  }

  /**
  * POST's all DTO's of a Part
  */
  createPartDetail() {
    this.isLoading = true;
    this.isSaving.next(true);
    if (this.partDataObject.get('PressId').value && this.partDataObject.get('Code').value) {
      if (!this.isDataObjectEmpty(this.containerDataObject) && !this.containerDataObject.get('Number').value) {
        this.cgpAlertDialog.showAlertDialog({
          title: this.saveContainerWarningText,
          message: `The container info will not be saved without a container id. Please enter a container id`,
          alert: 'danger',
          onClosed: () => {
            this.isLoading = false;
            this.edit();
          }
        });
      } else { this.postPartDetail(); }
    }
    else { this.isLoading = false; }
  }

  postPartDetail() {
    this.dieDataObject.get('PartId').setValue(defaultGuid);
    this.partHandlingDataObject.get('PartId').setValue(defaultGuid);
    this.partLoadingDataObject.get('PartId').setValue(defaultGuid);
    this.containerDataObject.get('PartId').setValue(defaultGuid);
    this.partLoadingDataObject.get('PressId').setValue(this.partDataObject.get('PressId').value);
    this.containerDataObject.get('IsActive').setValue(true);
    this.partDataObject.get('IsActive').setValue(true);
    this.fullPartDetail.get('Part').setValue(this.partDataObject.value);
    this.fullPartDetail.get('Container').setValue(this.containerDataObject.value);
    this.fullPartDetail.get('Die').setValue(this.dieDataObject.value);
    this.fullPartDetail.get('PartHandling').setValue(this.partHandlingDataObject.value);
    this.fullPartDetail.get('PartLoading').setValue(this.partLoadingDataObject.value);
    this.partService.save(this.fullPartDetail.value, true, '/GetFullPartDetail')
      .then(partResult => {
        if (partResult) {
          // Set PartId's back to each Data Object
          this.containerDataObject.get('PartId').setValue(partResult['Part']);
          this.dieDataObject.get('PartId').setValue(partResult['Part']);
          this.partHandlingDataObject.get('PartId').setValue(partResult['Part']);
          this.partLoadingDataObject.get('PartId').setValue(partResult['Part']);

          // Set Id's back to each Data Object respectively 
          this.partDataObject.get('Id').setValue(partResult['Part']);
          this.partDetailHeaderDataObject.get('Id').setValue(partResult['Part']);
          this.containerDataObject.get('Id').setValue(partResult['Container']);
          this.dieDataObject.get('Id').setValue(partResult['Die']);
          this.partHandlingDataObject.get('Id').setValue(partResult['PartHandling']);
          this.partLoadingDataObject.get('Id').setValue(partResult['PartLoading']);

          // Update Container and Part Images
          Promise.all([
            this.updateContainer(),
            this.updatePart(),
          ]).then(() => { 
            this.isLoading = false; 
            this.isSaving.next(false); 
          })
            .catch(() => { 
              this.isLoading = false; 
              this.isSaving.next(false); 
            })
        }
        else { 
          this.isLoading = false; 
          this.isSaving.next(false); 
        }
      });
  }

  /**
  * POST's a Container DTO from the container-tab formGroup
  */
  createContainer(): Promise<any> {
    if (!this.containerDataObject) {
      return Promise.resolve();
    } else if (
      !this.isDataObjectEmpty(this.containerDataObject) &&
      !this.containerDataObject.get('Number').value) {
      this.cgpAlertDialog.showAlertDialog({
        title: this.saveContainerWarningText,
        message: `The container info will not be saved without a container id. Please enter a container id`,
        alert: 'danger',
        onClosed: () => {
          this.isLoading = false;
          this.edit();
        }
      });
    } else {
      return this.containerService.save(this.containerDataObject.value, true, "")
        .then(x => {
          if (this.containerDataObject.get('AttachmentFileInfo').value) {
            this.uploadEventHandler('container', this.containerDataObject.get('AttachmentFileInfo').value);
            this.containerDataObject.get('Id').setValue(x.Id);
            return
          }
        })
    }
    return Promise.resolve();
  }

  /**
  * POST's a Die DTO from the die-tab formGroup
  */
  createDie(): Promise<any> {
    if (!this.dieDataObject) {
      return null;
    }
    return this.dieService.save(this.dieDataObject.value, true, "")
  }

  /**
  * POST's a PartHandling DTO from the PartHandling-tab formGroup
  */
  createPartHandling(): Promise<any> {
    if (!this.partHandlingDataObject) {
      return null;
    }
    return this.partHandlingService.save(this.partHandlingDataObject.value, true, "")
  }

  /**
  * POST's a PartLoading DTO from the PartLoading-tab formGroup
  */
  createPartLoading(): Promise<any> {
    if (!this.partLoadingDataObject) {
      return null;
    }
    return this.partLoadingService.save(this.partLoadingDataObject.value, true, "")
  }

  /**
  * Displays a confirmation dialog when reallocating an existing Part to a new Press
  */
  displayPartReallocationDialog() {
    this.isLoading = true;

    const part = this.partDataObject.value;
    const pressId = part.PressId;
    const partCode = part.Code;
    const press = this.partDetailHeaderDataObject.get('PressDescription').value;

    this.cgpConfirmationDialog.showConfirmationDialog({
      title: 'Save Confirmation',
      message: `Part - ${partCode} will be copied and reallocated to Press - ${press}. Would you like to continue?`,
      alert: 'danger',
      onYesClick: () => {
        if (pressId && partCode) {
          if (part.ToolSetId) {
            this.createDuplicateToolSetParts();
          }
          else {
            this.createDuplicatePart(part);
          }
        }
        this.isLoading = false;
        this.cgpConfirmationDialog.closeConfirmationDialog();
      },
      onNoClick: () => {
        this.isLoading = false;
        this.cgpConfirmationDialog.closeConfirmationDialog();
      }
    });
  }

  readonly createDuplicateToolSetParts = () => {
    const parentPart = this.partDetailHeaderDataObject.get('Parentpart').value;
    const toolSetId = this.partDetailHeaderDataObject.get('ToolSetId').value;
    const toolSetCode = this.partDetailHeaderDataObject.get('ToolSetCode').value;
    const currentPartsList = this.partDetailHeaderDataObject.get('Parts').value;
    const newPressId = this.partDataObject.get('PressId').value;
    const newPartsList: any[] = currentPartsList;

    this.toolSetService.save(currentPartsList, true, '/DuplicateParts/press/' + newPressId, true)
      .then(result => {
        result.forEach(function (newPart) {
          newPartsList.push(newPart);
        });

        const toolSetRequestBody = {
          "ToolSetId": toolSetId,
          "Code": toolSetCode,
          "Parts": newPartsList,
          "Parentpart": parentPart
        }

        this.toolSetService.save(toolSetRequestBody, false, '/' + toolSetId);
      })
      .catch(e => { this.isLoading = false })
  }

  /**
  * POST's a duplicate of an existing Part with a new PressId
  */
  createDuplicatePart(part: any): any {
    part.PressId = this.partDataObject.get('PressId').value; //allocate duplicate part's press to the newly selected press
    let die, partHandling, partLoading,  press;
    const container = this.containerDataObject.value;

    return this.getPressLine(part.PressId).then(pressResult => {
      press = pressResult;

      return this.getDie(part).then(dieResult => {
        die = dieResult;
  
        return this.getPartHandling(part).then(partHandlingResult => {
          partHandling = partHandlingResult;
  
          return this.getPartLoading(part).then(partLoadingResult => {
            partLoading = partLoadingResult;
            part.PartLoadingId = partLoadingResult.Id;
  
            return this.savePart({
              part,
              die,
              partHandling,
              partLoading,
              container
            }, press);
          })
            .catch(e => { this.isLoading = false });
        })
          .catch(e => { this.isLoading = false });
      })
        .catch(e => { this.isLoading = false });
    })
      .catch(e => { this.isLoading = false });
  }


  private savePart(obj: any, press: any): Promise<any> {
    return this.partService.save(obj.part, true, "", true)
      .then(partId => {

        if (obj.die) {
          this.setDuplicateDieValues(obj.die, partId);
          this.dieService.save(obj.die, true);
        }

        if (obj.partHandling) {
          this.setDuplicatePartHandlingValues(obj.partHandling, partId, press);
          this.partHandlingService.save(obj.partHandling, true);
        }

        if (obj.partLoading) {
          this.setDuplicatePartLoadingValues(obj.partLoading, partId, obj.part.PressId);
          this.partLoadingService.save(obj.partLoading, true);
        }

        if (obj.container) {
          obj.container.partId = partId;
          this.containerService.save(obj.container, true)
            .then(containerId => { obj.part.ContainerId = containerId });
        }
      })
      .catch(e => { this.isLoading = false });
  }


  getDie(part): any {
    return this.dieService.read('/part/' + part.Id)
      .then(result => {
        return result;
      });
  }

  getPartHandling(part): any {
    return this.partHandlingService.read('/part/' + part.Id)
      .then(result => {
        return result;
      });
  }

  getPartLoading(part): any {
    return this.partLoadingService.read(`/${part.Id}/${part.PressId}`)
      .then(result => {
        return result;
      });
  }

  getPressLine(pressId): any {
    return this.pressService.read(`/${pressId}`)
    .then(result => {
      return result;
    })
  }

  /**
  * When creating a duplicate Part with a new PressId, 
  * the Die information associated with the original Part should be preserved, 
  * EXCEPT ToolingRestriction, ToolingRestrictionNote, and SPMDeviation
  */
  setDuplicateDieValues(die: any, partId: any) {
    die.PartId = partId;
    die.ToolingRestriction = null;
    die.ToolingRestrictionNote = null;
    die.SPMDeviation = null;
  }

  /**
  * When creating a duplicate Part with a new PressId, 
  * the PartHandling information associated with the original Part should not be preserved
  */
  setDuplicatePartHandlingValues(partHandling: any, partId: any, press: any) {
    partHandling.PartId = partId;
    partHandling.PresentationId = null;
    partHandling.PartsPerLift = 0;
    partHandling.PeoplePerLift = 0;
    partHandling.WalkDistance = 0;
    partHandling.SortingDisplacement = 0;
    partHandling.NumberPeopleLoading = 0;
    partHandling.NumberPeopleSorting = 0;
    partHandling.Assist = press.AvailableAssist ? partHandling.Assist : false;
    partHandling.ManualSorting = true;
    partHandling.Presentation = null;
    partHandling.TotalNumberOfUtilizedWorkStations = 0;
    partHandling.IsActive = null;
  }

  /**
  * When creating a duplicate Part with a new PressId, 
  * the PartLoading information associated with the original Part should not be preserved
  * EXCEPT for PartId, PressId, and OrientationInDie
  */
  setDuplicatePartLoadingValues(partLoading: any, partId: any, pressId: any) {
    partLoading.PressId = pressId;
    partLoading.PartId = partId;
    partLoading.ContainerExchange = null;
    partLoading.ContainerLimits = null;
    partLoading.TravelTime = 0;
    partLoading.TruckAccess = 0;
    partLoading.ContainersPerTrip = 0;
    partLoading.AvailableTrucks = 0;
    partLoading.UnloadingMethod = null;
    partLoading.FirstOrientation = null;
    partLoading.OrientationOnEOL = null;
    partLoading.OrientationInContainer = null;
    partLoading.DistributionMethod = null;
    partLoading.LoadingMethod = null;
    partLoading.OrientationCheck = true;
  }

  /**
  * Upload selected image for Part and/or Container to S3
  */
  uploadEventHandler = (item: any, fileInfo: any[]) => {
    const s3directory = (item.toLowerCase() === 'part') ? 'PartPictures' : 'ContainerPictures';
    const s3Path = `PHF/${s3directory}/`;
    const requestBody = {
      "FileName": fileInfo[0].name,
      "FileDescription": '',
      "Path": s3Path
    }

    this.s3Service.handleS3Upload(fileInfo, s3Path)
      .then(x => {
        switch (item.toLowerCase()) {
          case 'part':
            this.uploadPartImage(requestBody, s3Path, fileInfo);
            break;
          case 'container':
            this.uploadContainerImage(requestBody, s3Path, fileInfo);
            break;
          default:
            break;
        }
      })
      .catch(e => this.cgpAlertDialog.showAlertDialog(
        {
          title: 'Upload Error: ',
          alert: 'danger',
          message: e.error,
          closeLabel: 'Close',
        }
      ));
  }

  uploadPartImage(requestBody: any, s3Path: any, fileInfo: any[]) {
    const partId = this.partDataObject.get('Id').value;
    const partAttachmentId = this.partDataObject.get('AttachmentId').value
    let isNewAttachment: boolean;

    if (!partAttachmentId) { isNewAttachment = true; }
    else { isNewAttachment = false; }

    const uploadPath = '/UploadPartAttachment?partId=' + partId;
    const updatePath = '/UpdatePartAttachment/' + partId;
    const servicePath = isNewAttachment ? uploadPath : updatePath;

    this.partService.save(requestBody, isNewAttachment, servicePath, true)
      .then(attachmentId => {
        this.partDataObject.get('AttachmentId').setValue(attachmentId);
        const filePath = s3Path + fileInfo[0].name;
        const imageUrl = this.s3Service.getS3Path(filePath);
        this.partDataObject.get('AttachmentPath').setValue(imageUrl);
        this.partDataObject.get('AttachmentFileName').setValue(fileInfo[0].name);
        this.partDetailHeaderDataObject.get('Attachment').setValue(imageUrl);
      });
  }

  uploadContainerImage(requestBody: any, s3Path: any, fileInfo: any[]) {
    const containerId = this.containerDataObject.get('Id').value;
    let isNewAttachment: boolean;

    this.containerService.read('/' + containerId).then(response => {
      if (!response.AttachmentId) { isNewAttachment = true; }
      else { isNewAttachment = false; }

      const uploadPath = '/UploadContainerAttachment?containerId=' + containerId;
      const updatePath = '/UpdateContainerAttachment/' + containerId;
      const servicePath = isNewAttachment ? uploadPath : updatePath;

      this.containerService.save(requestBody, isNewAttachment, servicePath, true)
        .then(attachmentId => {
          this.containerDataObject.get('AttachmentId').setValue(attachmentId);
          const filePath = s3Path + fileInfo[0].name;
          const imageUrl = this.s3Service.getS3Path(filePath);
          this.containerDataObject.get('Attachment').setValue(imageUrl);
        });
    });
  }

  isDataObjectEmpty(dataObject: any): boolean {
    const controls = dataObject.controls
    const fields = ["Attachment", "AttachmentFileInfo", "AttachmentId", "PartId", "IsActive", "Id"];
    for (const name in controls) {
      if (fields.indexOf(name) === -1 && controls[name].value) return false;
    }
    return true;
  }
}

const fullPartDetailEditForm = {
  Part: [],
  Container: [],
  Die: [],
  PartHandling: [],
  PartLoading: [],
}
